import { FC, useState } from 'react';
import styles from './PurchaseCard.module.scss';
import { images } from '../../constants/images';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useNavigate } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import { PurchaseCardProps } from './interfaces';
import ModalPaymentReserve from '../PublicationDetail/components/ModalPaymentReserve';
import useMediaQuery from '@mui/material/useMediaQuery';

const PurchaseCard: FC<PurchaseCardProps> = ({
    publication,
    showCancelButton = false,
    showRescheduleButton = false,
    onCancel,
    onReschedule,
    onClick,
    onTitleClick,
    currentStep,
    steps,
    openModalPaymentReserve: openModalPaymentReserveFromProps,
    setOpenModalPaymentReserve: setOpenModalPaymentReserveFromProps,
    user,
    getDataPayment,
    dataPayment,
    getPaymentProcess,
    getCancellationPayment,
    getReservePublicationId,
    reservar,
    publicationId
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [openModalPaymentReserveLocal, setOpenModalPaymentReserveLocal] = useState(false);
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:768px)');

    const openModalPaymentReserve = openModalPaymentReserveLocal;
    const setOpenModalPaymentReserve = setOpenModalPaymentReserveLocal;

    const formatPrice = (value?: number) => {
        if (!value) return '$0';
        return `$${value.toLocaleString('es-CL')}`;
    };

    const imageUrl = publication.publication?.pictures?.[0]?.path || images.noImagePublication;
    const title = publication.publication?.title || 'Sin título';
    const year = publication.publication?.vehicle_data?.year || publication.publication?.primary_description?.split(' ')[0] || '';
    const mileage = publication.publication?.vehicle_data?.mileage || publication.publication?.secondary_description || '0';
    const visitConfirmed = publication?.inspection_schedule && 
        Object.keys(publication.inspection_schedule).length > 0 &&
        publication.inspection_schedule.date_start;
    const visitDate = publication?.inspection_schedule ? 
        new Date(publication.inspection_schedule.date_start).toLocaleDateString('es-CL', {
            weekday: 'short',
            day: 'numeric',
            month: 'short'
        }) : '';
    const visitTime = publication?.inspection_schedule ? 
        new Date(publication.inspection_schedule.date_start).toLocaleTimeString('es-CL', {
            hour: '2-digit',
            minute: '2-digit'
        }) : '';
    const visitAddress = publication?.inspection_schedule?.name_inspection_point && publication?.inspection_schedule?.commune ? 
        `${publication.inspection_schedule.name_inspection_point}, ${publication.inspection_schedule.commune}` : '';
    const price = publication.publication?.price;
    const counteroffer = publication?.record_prices?.find((r: any) => r.title === 'COUNTEROFFER');
    const offerPrice = counteroffer ? counteroffer.new_price : null;
    const originalPrice = counteroffer ? counteroffer.old_price : price;
    
    const getNormalizedStatus = () => {
        if (publication.flows && publication.flows.length > 0) {
            const flowCode = publication.flows[0]?.code;
            switch (flowCode) {
                case 'new_visit':
                    return 'pending';
                case 'scheduled_visit':
                    return 'pending';
                case 'cancelled_visit':
                    return 'cancelled';
                case 'in_progress_visit':
                    return 'in_progress';
                case 'payment_made':
                    return 'payment';
                case 'rescheduled':
                    return 'rescheduled';
                case 'not_completed_visit':
                    return 'in_progress';
                case 'completed_visit':
                    return 'finished';
                default:
                    return publication.status || 'pending';
            }
        }
        return publication.status || 'pending';
    };

    const status = getNormalizedStatus();

    const purchaseSteps = [
        { label: 'Pendiente' },
        { label: 'En progreso' },
        { label: 'Pago' },
        { label: 'Finalizado' },
    ];

    const getActiveStep = (status: string) => {
        switch (status) {
            case 'pending': return 0;
            case 'in_progress': return 1;
            case 'payment': return 2;
            case 'finished': return 3;
            case 'cancelled': return 0;
            case 'rescheduled': return 0;
            default: return 0;
        }
    };

    const activeStep = getActiveStep(status);

    const handleCardClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isMobile) {
            setIsExpanded(!isExpanded);
        }
    };

    const handleExpandClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isMobile) {
            setIsExpanded(!isExpanded);
        }
    };

    return (
        <>
            <div 
                className={`${styles.purchaseCard} ${isExpanded || isMobile ? styles.expanded : ''}`} 
                onClick={handleCardClick}
                onDragStart={(e) => e.preventDefault()} 
            >
            <div className={styles.cardColumns}>
                <div className={styles.imageContainer}>
                    <img 
                        src={imageUrl} 
                        alt={title}
                        className={styles.vehicleImage}
                    />
                </div>
                
                <div className={styles.contentContainer}>
                    <div className={styles.header}>
                        <div className={styles.titleSection}>
                            <h3 
                                className={styles.title} 
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (publication.publication?._id) {
                                        navigate(`/publicacion/${publication.publication._id}`);
                                    }
                                }}
                            >
                                {title}
                            </h3>
                            <div className={styles.subtitle}>
                                <span>{year}</span>
                                <span className={styles.separator}>|</span>
                                <span>{mileage} km</span>
                            </div>
                        </div>

                        <div className={styles.priceSection}>
                            {offerPrice !== null ? (
                                <div className={styles.counterofferPrices}>
                                    <p className={styles.oldPrice}>{formatPrice(originalPrice)}</p>
                                    <p className={styles.price} style={{ margin: 0 }}>{formatPrice(offerPrice)}</p>
                                </div>
                            ) : (
                                <p className={styles.price}>{formatPrice(price)}</p>
                            )}
                             
                                <div className={`${styles.confirmedBadge} ${status === 'pending' ? styles.pendingBadge : status === 'cancelled' ? styles.cancelledBadge : ''}`}>
                                    <span>
                                        {status === 'pending' ? 'Esperando confirmación' : 
                                         status === 'finished' ? 'Visita realizada' :
                                         status === 'cancelled' ? 'Visita cancelada' :
                                         'Visita confirmada'}
                                    </span>
                                </div>
                            
                        </div>
                    </div>


                    <div className={styles.visitInfo}>
                        <div className={styles.statusBadge}>
                            {status === 'pending' ? (
                                <>
                                    <img src={images.calendarPending} alt="calendar-pending" className={styles.statusIcon} />
                                    <span>PROPUESTA DE VISITA ENVIADA</span>
                                </>
                            ) : status === 'cancelled' ? (
                                <>
                                    <CancelOutlinedIcon className={styles.statusIcon} />
                                    <span>VISITA CANCELADA</span>
                                </>
                            ) : status === 'finished' ? (
                                <>
                                    <CheckCircleOutlinedIcon className={styles.statusIcon} />
                                    <span>VISITA REALIZADA</span>
                                </>
                            ) : (
                                <>
                                    <CheckCircleOutlinedIcon className={styles.statusIcon} />
                                    <span>VISITA CONFIRMADA</span>
                                </>
                            )}
                        </div>

                        {visitConfirmed && (
                            <div className={styles.visitDetails}>
                                {visitDate && (
                                    <div className={`${styles.detailItem} ${status === 'cancelled' ? styles.cancelled : ''}`}>
                                        <CalendarTodayIcon className={styles.icon} />
                                        <span>{visitDate}</span>
                                    </div>
                                )}

                                {visitTime && (
                                    <div className={`${styles.detailItem} ${status === 'cancelled' ? styles.cancelled : ''}`}>
                                        <AccessTimeIcon className={styles.icon} />
                                        <span>{visitTime}</span>
                                    </div>
                                )}

                                {visitAddress && (
                                    <div className={`${styles.detailItem} ${status === 'cancelled' ? styles.cancelled : ''}`}>
                                        <LocationOnIcon className={styles.icon} />
                                        <span>{visitAddress}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                   {/* {status !== 'cancelled' && status !== 'finished' && (
                    <div className={styles.cancelVisitButton}>
                        <button 
                            className={styles.cancelButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                onCancel?.();
                            }}
                        >
                            <CloseIcon className={styles.buttonIcon} />
                            Cancelar visita
                        </button>
                    </div>
                )} */}
                </div>

                {!isMobile && (
                    <button 
                        className={`${styles.expandButton} ${isExpanded ? styles.expanded : ''}`}
                        onClick={handleExpandClick}
                    >
                        <KeyboardArrowDownIcon />
                    </button>
                )}
            </div>

            {(isExpanded || isMobile) && (
                <div className={styles.stepperContainer}>
                    <div className={`${styles.stepper} ${status === 'cancelled' ? styles.cancelledStepper : ''}`}>
                        {purchaseSteps.map((step, index) => (
                            <div key={index} className={styles.stepWrapper}>
                                <div className={styles.stepItem}>
                                    <div className={`${styles.stepDot} ${index === activeStep ? styles.active : ''} ${index < activeStep ? styles.completed : ''} ${status === 'cancelled' ? styles.cancelledDot : ''}`}>
                                        {index === activeStep && <div className={styles.innerDot} />}
                                        {index < activeStep && (
                                            <CheckIcon style={{ color: 'white', fontSize: '18px' }}/>
                                        )}
                                    </div>
                                    <span className={`${styles.stepLabel} ${index === activeStep ? styles.active : ''} ${status === 'cancelled' ? styles.cancelledLabel : ''}`}>
                                        {step.label}
                                    </span>
                                </div>
                                {index < purchaseSteps.length - 1 && (
                                    <div className={`${styles.stepLine} ${index < activeStep ? styles.active : ''} ${status === 'cancelled' ? styles.cancelledLine : ''}`} />
                                )}
                            </div>
                        ))}
                    </div>

                    {/* {(status === 'finished' || status === 'in_progress' ) && (
                        <div className={styles.actionSection}>
                            <div className={styles.actionContent}>
                                <div className={styles.actionIcon}>
                                    {status === 'finished' ? (
                                        <FolderZipOutlinedIcon className={styles.actionSectionIcon} />
                                    ) : (
                                        <ShieldOutlinedIcon className={styles.actionSectionIcon} />
                                    )}
                                </div>
                                <span className={styles.actionText}>
                                    {status === 'finished' ? 'Acta de entrega disponible' : 
                                     status === 'in_progress' ? 'Inspección verificada — $39.990' : ''}
                                </span>
                            </div>

                            {status === 'in_progress' && (
                                <div className={styles.fullmecLogo}>
                                    <img src={images.fullmecLogoService} alt="Fullmec" />
                                </div>
                            )}

                            <div className={styles.actionButtons}>
                                {status === 'finished' && (
                                    <button className={styles.downloadButton}>
                                        <DownloadIcon style={{ fontSize: 16 }} />
                                        Descargar
                                    </button>
                                )}
                                {status === 'in_progress' && (
                                    <>
                                        <button 
                                            className={styles.payButton}
                                            onClick={(e) => { 
                                                e.stopPropagation();
                                                setOpenModalPaymentReserve?.(true);
                                            }}
                                        >
                                           Pagar
                                        </button>
                                    </>
                                )}
                                
                            </div>
                        </div>
                    )} */}
                </div>
            )}
        </div>

        <ModalPaymentReserve
            open={openModalPaymentReserve}
            onClose={() => setOpenModalPaymentReserve?.(false)}
            vehiclePrice={publication.publication?.price}
            depositAmount={200000}
            setOpenModalForm={() => {}}
            reservar={reservar}
            getReservePublicationId={getReservePublicationId}
            user={user}
            publication={publication}
            getPaymentProcess={getPaymentProcess}
            dataPayment={dataPayment}
            getDataPayment={getDataPayment}
            getCancellationPayment={getCancellationPayment}
            publicationId={publicationId}
        />
        </>
    );
};

export default PurchaseCard;